import { ObjectTemplateGenerationEnum } from '@celito.clients/enums';
import { files } from '@celito.clients/services';
import { FluentIconNames, Icon } from '@celito.clients/shared';
import {
  Menu as FluentMenu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import {
  DocumentBulletListRegular,
  DocumentPdfRegular,
} from '@fluentui/react-icons';
import React from 'react';

import { generateFile } from '../utils/apiService';
import { ObjectTemplatesSubMenuProps } from './object-template-menu.model';
import { submenuStyles } from './object-template-menu.styles';

export const ObjectTemplatesSubMenu = ({
  menuItem,
  objectMetadata,
  rowData,
}: ObjectTemplatesSubMenuProps) => {
  const styles = submenuStyles();

  if (!objectMetadata?.allowObjectTemplates) {
    return null;
  }

  const activeTemplates = objectMetadata?.objectTemplates?.filter(
    (template) =>
      template.isActive &&
      template?.availableFormats &&
      template?.availableFormats?.length > 0
  );

  if (activeTemplates.length === 0) {
    return null;
  }

  const getFormatIcon = (format: string) => {
    switch (format) {
      case ObjectTemplateGenerationEnum.PDF:
        return <DocumentPdfRegular />;
      case ObjectTemplateGenerationEnum.DOCX:
        return <DocumentBulletListRegular />;
      default:
        return <DocumentBulletListRegular />;
    }
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case ObjectTemplateGenerationEnum.PDF:
        return 'PDF';
      case ObjectTemplateGenerationEnum.DOCX:
        return 'DOCX';
      default:
        return format.toUpperCase();
    }
  };

  const handleDownload = async (templateName: string, format: string) => {
    // Avoid stringifying objects like "[object Object]". Only include version if it's a primitive.
    const rawVersion = rowData?.version;
    const versionStr =
      typeof rawVersion === 'string' || typeof rawVersion === 'number'
        ? String(rawVersion)
        : '';

    const versionSuffix = versionStr ? `_v${versionStr}` : '';
    const fileName = `${rowData?.label}${versionSuffix}.${format}`;
    await files.downloadFile(
      generateFile({
        objectName: objectMetadata.name,
        templateName,
        format,
        recordName: rowData?.name as string,
        // Only pass version as a string when it's a primitive; otherwise omit/empty
        version: versionStr || undefined,
      }),
      (fileDocument) => {
        files.downloadFileBuffer({
          data: new Uint8Array(fileDocument.data),
          label: fileName,
        });
      }
    );
  };

  return (
    <FluentMenu>
      <MenuTrigger disableButtonEnhancement>
        <MenuItem key={menuItem.label}>
          <div className={styles.itemContent}>
            <Icon iconName={menuItem.iconName as FluentIconNames} />
            {menuItem.label}
          </div>
        </MenuItem>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          <MenuGroup>
            {activeTemplates.map((template, index) => {
              return (
                <React.Fragment key={template.name}>
                  <MenuGroup>
                    <MenuGroupHeader
                      style={{
                        paddingInline: '2px',
                        height: '20px',
                        color: 'var(--color-neutral-foreground)',
                      }}
                    >
                      {template.label}
                    </MenuGroupHeader>
                    {template.availableFormats?.map((format: string) => (
                      <MenuItem
                        key={`${template.name}-${format}`}
                        onClick={() => handleDownload(template.name, format)}
                        icon={getFormatIcon(format)}
                      >
                        {getFormatLabel(format)}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                  {index !== activeTemplates.length - 1 && <MenuDivider />}
                </React.Fragment>
              );
            })}
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </FluentMenu>
  );
};
